import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import {
  ListaMobile,
  ItemLista,
  IconeQuadrado,
  GradeNumeros,
} from '@/components/common/ListaMobile';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import axios from 'axios';
import {
  Plus,
  X,
  Send,
  Bell,
  AlertTriangle,
  Image as ImageIcon,
  Trash2,
  BarChart3,
  Megaphone,
  RefreshCw,
  ClipboardList,
  Eye,
  MoreVertical,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TableSkeleton } from '@/components/common/Skeletons';
import {
  useNotificationsList,
  useCreateNotification,
  useDispatchNotification,
  useDeleteNotification,
  useDeviceInsights,
} from '@/hooks/useNotificationBanners';
import { CREATABLE_TYPES, notificationsApi } from '@/api/endpoints/notifications';
import type {
  Notification,
  NotificationInput,
  NotificationType,
  NotificationImage,
} from '@/api/endpoints/notifications';

type FormState = {
  type: NotificationType;
  title: string;
  body: string;
  images: NotificationImage[];
  items: string[];
  link: string;
  force: boolean;
  sendPush: boolean;
  minVersion: string;
  priority: number;
};

const emptyForm: FormState = {
  type: 'ad',
  title: '',
  body: '',
  images: [],
  items: [],
  link: '',
  force: false,
  sendPush: false,
  minVersion: '',
  priority: 0,
};

function formToInput(form: FormState): NotificationInput {
  const items = form.items.map((i) => i.trim()).filter(Boolean);
  return {
    type: form.type,
    title: form.title.trim(),
    // 'update' não usa texto livre — o corpo do push é as novidades juntas.
    body: form.type === 'update' ? items.join('\n') : form.body.trim(),
    images: form.type === 'ad' ? form.images.filter((img) => img.url.trim()) : undefined,
    // 'update' usa items como bullets de novidade; 'ad' reaproveita o mesmo
    // campo pros destaques (formato "valor|rótulo") que aparecem no card.
    items: form.type === 'update' || form.type === 'ad' ? items : undefined,
    link: form.link.trim() || undefined,
    force: form.type === 'update' ? form.force : undefined,
    sendPush: form.type === 'ad' || form.type === 'update' ? form.sendPush : undefined,
    minVersion: form.type === 'update' ? form.minVersion.trim() || undefined : undefined,
    priority: form.priority,
  };
}

const TYPE_LABEL: Record<NotificationType, string> = {
  ad: 'Anúncio',
  update: 'Atualização',
  task: 'Manejo pendente',
  push: 'Push',
};

const TYPE_BADGE: Record<NotificationType, string> = {
  ad: 'bg-primary/10 text-primary',
  update: 'bg-warning/10 text-warning',
  task: 'bg-muted text-text-secondary',
  push: 'bg-info/10 text-info',
};

// Lista do celular: ícone e cor por tipo, como no desenho aprovado.
const TYPE_MOBILE: Record<
  NotificationType,
  { icone: LucideIcon; quadrado: string; texto: string }
> = {
  ad: { icone: Megaphone, quadrado: 'bg-primary/15 text-primary', texto: 'text-primary' },
  update: { icone: RefreshCw, quadrado: 'bg-tertiary/15 text-tertiary', texto: 'text-tertiary' },
  push: { icone: Bell, quadrado: 'bg-warning/15 text-warning', texto: 'text-warning' },
  task: {
    icone: ClipboardList,
    quadrado: 'bg-muted text-text-secondary',
    texto: 'text-text-secondary',
  },
};

// Forma de entrega em texto curto (a coluna Ações da tabela mostra o mesmo).
function deliveryLabel(notification: Notification): string {
  if (notification.type === 'task') return 'Automático';
  if (notification.type === 'push') return 'Push';
  return notification.sendPush ? 'Push e banner' : 'Só banner';
}

// 'push' sempre dispara. 'ad'/'update' só se o admin marcou o checkbox de
// push ao criar. 'task' não tem disparo manual (fica pro sistema
// automático, futuro).
function canManualDispatch(notification: Notification): boolean {
  if (notification.type === 'push') return true;
  if (notification.type === 'ad' || notification.type === 'update') return notification.sendPush;
  return false;
}

function InsightCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  tone?: 'warning';
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wider text-text-secondary">{label}</p>
      <p
        className={`mt-1 text-2xl font-bold tabular-nums ${tone === 'warning' ? 'text-warning' : 'text-text-title'}`}
      >
        {value ?? '—'}
      </p>
      {hint && <p className="mt-0.5 text-xs text-text-hint">{hint}</p>}
    </div>
  );
}

const menuItemClass =
  'flex w-full items-center gap-3 whitespace-nowrap px-4 py-2.5 text-left text-sm text-text-body transition-colors hover:bg-muted focus:outline-none focus-visible:bg-muted disabled:opacity-50';

// Menu de três pontos da linha no celular, com as mesmas ações da coluna
// Ações da tabela. Vai num portal com posição fixa: a ListaMobile corta o que
// passa da borda (overflow-hidden), igual ao UserActionsMenu.
function MenuAcoesNotificacao({
  notification,
  dispatching,
  engagementLoading,
  onDispatch,
  onEngagement,
  onDelete,
}: {
  notification: Notification;
  dispatching: boolean;
  engagementLoading: boolean;
  onDispatch: () => void;
  onEngagement: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const podeDisparar = canManualDispatch(notification);
  const temEngajamento = Boolean(notification.lastDispatchedAt);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (!menuRef.current?.contains(alvo) && !buttonRef.current?.contains(alvo)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const toggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = 8 + 44 * (1 + Number(podeDisparar) + Number(temEngajamento));
      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;
      if (top + menuHeight > window.innerHeight) top = rect.top - menuHeight - 4;
      if (left < 8) left = 8;
      setPos({ top, left });
    }
    setOpen((v) => !v);
  };

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label="Mais ações"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex size-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-muted hover:text-text-title focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <MoreVertical size={18} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 w-56 overflow-hidden rounded-xl border border-border bg-bg-elevated py-1 shadow-elevated animate-fade-up"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {podeDisparar && (
              <button
                role="menuitem"
                onClick={() => run(onDispatch)}
                disabled={dispatching}
                className={menuItemClass}
              >
                <Send size={16} className="text-primary" />
                {dispatching ? 'Disparando...' : 'Disparar'}
              </button>
            )}
            {temEngajamento && (
              <button
                role="menuitem"
                onClick={() => run(onEngagement)}
                disabled={engagementLoading}
                className={menuItemClass}
              >
                <BarChart3 size={16} className="text-text-secondary" />
                Ver engajamento (GA4)
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => run(onDelete)}
              className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2.5 text-left text-sm font-medium text-error transition-colors hover:bg-error/10 focus:outline-none focus-visible:bg-error/10"
            >
              <Trash2 size={16} />
              Excluir
            </button>
          </div>,
          document.body
        )}
    </>
  );
}

const inputClass =
  'w-full rounded-lg border border-border bg-bg-elevated px-4 py-2 text-sm text-text-body placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-2 block text-sm font-medium text-text-title';

// Conteúdo testável, separado do <Layout> (que monta Sidebar/Header e exige
// AuthProvider + window.matchMedia) — mesmo padrão do ProducerPanel dentro
// da página de Support: a página (./index.tsx) só faz a moldura.
export function NotificationsPanel() {
  const { data: notifications, isLoading, error } = useNotificationsList();
  const createMutation = useCreateNotification();
  const dispatchMutation = useDispatchNotification();
  const deleteMutation = useDeleteNotification();
  const { data: deviceInsights } = useDeviceInsights();

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [dispatchingId, setDispatchingId] = useState<number | null>(null);
  const [engagementLoadingId, setEngagementLoadingId] = useState<number | null>(null);

  const openCreate = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const closeModal = () => {
    if (createMutation.isPending) return;
    setShowModal(false);
  };

  const addImage = () => setForm({ ...form, images: [...form.images, { url: '', link: '' }] });
  const removeImage = (index: number) =>
    setForm({ ...form, images: form.images.filter((_, i) => i !== index) });
  const updateImage = (index: number, patch: Partial<NotificationImage>) =>
    setForm({
      ...form,
      images: form.images.map((img, i) => (i === index ? { ...img, ...patch } : img)),
    });

  const addItem = () => setForm({ ...form, items: [...form.items, ''] });
  const removeItem = (index: number) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  const updateItem = (index: number, value: string) =>
    setForm({ ...form, items: form.items.map((it, i) => (i === index ? value : it)) });

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert('Título é obrigatório');
      return;
    }
    if (form.type === 'update' && !form.items.some((i) => i.trim())) {
      alert('Adicione ao menos uma novidade');
      return;
    }
    if (form.type !== 'update' && !form.body.trim()) {
      alert('Corpo é obrigatório');
      return;
    }
    try {
      await createMutation.mutateAsync(formToInput(form));
      setShowModal(false);
    } catch (err) {
      console.error('Erro ao criar notificação:', err);
      alert('Erro ao criar a notificação. Verifique os dados e tente novamente.');
    }
  };

  const handleDispatch = async (notification: Notification) => {
    if (
      !confirm(
        `Disparar "${notification.title}" agora? Vai chegar via push pra quem tiver token registrado.`
      )
    ) {
      return;
    }
    setDispatchingId(notification.id);
    try {
      const result = await dispatchMutation.mutateAsync(notification.id);
      alert(`Disparado: ${result.sent} entregues, ${result.failed} falharam.`);
    } catch (err) {
      const reason = axios.isAxiosError(err)
        ? (err.response?.data as { reason?: string })?.reason
        : undefined;
      const messages: Record<string, string> = {
        not_configured: 'Push não configurado no servidor (FIREBASE_SERVICE_ACCOUNT_JSON ausente).',
        not_found: 'Notificação não encontrada.',
        no_devices: 'Nenhum device com token de push pro alvo dessa notificação.',
      };
      alert((reason && messages[reason]) ?? 'Erro ao disparar a notificação. Tente novamente.');
      console.error('Erro ao disparar notificação:', err);
    } finally {
      setDispatchingId(null);
    }
  };

  const handleDelete = async (notification: Notification) => {
    if (!confirm(`Excluir "${notification.title}"? Não dá pra desfazer.`)) return;
    try {
      await deleteMutation.mutateAsync(notification.id);
    } catch (err) {
      console.error('Erro ao excluir notificação:', err);
      alert('Erro ao excluir a notificação. Tente novamente.');
    }
  };

  const handleEngagement = async (notification: Notification) => {
    setEngagementLoadingId(notification.id);
    try {
      const result = await notificationsApi.engagement(notification.id);
      if (!result.available) {
        alert(
          'Taxa de entrega/abertura do GA4 indisponível ainda (a dimensão "label" precisa ser registrada uma vez no GA4 Admin, ou os dados podem levar algumas horas pra aparecer).'
        );
        return;
      }
      alert(
        `Via GA4 (Firebase Analytics): ${result.received} recebidas, ${result.opened} abertas.`
      );
    } catch (err) {
      console.error('Erro ao buscar engajamento:', err);
      alert('Erro ao buscar o engajamento. Tente novamente.');
    } finally {
      setEngagementLoadingId(null);
    }
  };

  const list = notifications ?? [];
  const totals = list.reduce(
    (acc, n) => ({
      sent: acc.sent + (n.lastSent ?? 0),
      failed: acc.failed + (n.lastFailed ?? 0),
      read: acc.read + n.readCount,
    }),
    { sent: 0, failed: 0, read: 0 }
  );

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-error/10">
            <AlertTriangle size={26} className="text-error" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-text-title">
            Erro ao carregar notificações
          </h2>
          <p className="text-sm text-text-secondary">Não foi possível carregar a lista.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
            Notificações
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Anúncio (banner), Atualização (push + banner por versão) e Push (aviso genérico). Manejo
            pendente é gerado automaticamente pelo sistema.
          </p>
        </div>
        <Button onClick={openCreate} className="font-semibold max-md:hidden">
          <Plus size={18} />
          <span>Nova notificação</span>
        </Button>
        <BotaoFlutuante icon={Plus} label="Nova notificação" onClick={openCreate} />
      </div>

      {/* Desktop: cinco cartões. Celular/tablet: grade 2×2 + linha dos inativos. */}
      <div role="group" aria-label="Indicadores" className="hidden gap-4 lg:grid lg:grid-cols-5">
        <InsightCard label="Enviados" value={totals.sent} />
        <InsightCard
          label="Falharam"
          value={totals.failed}
          tone={totals.failed > 0 ? 'warning' : undefined}
        />
        <InsightCard label="Total de lidos" value={totals.read} />
        <InsightCard
          label="Dispositivos ativos"
          value={deviceInsights?.activeLast30d}
          hint={deviceInsights ? `de ${deviceInsights.totalDevices} no total` : undefined}
        />
        <InsightCard
          label="Inativos (30d+)"
          value={deviceInsights?.inactiveLast30d}
          tone={deviceInsights && deviceInsights.inactiveLast30d > 0 ? 'warning' : undefined}
        />
      </div>
      <div role="group" aria-label="Indicadores" className="flex flex-col gap-2 lg:hidden">
        <GradeNumeros
          colunas={2}
          itens={[
            { rotulo: 'Enviados', valor: totals.sent },
            {
              rotulo: 'Falharam',
              valor: totals.failed,
              cor: totals.failed > 0 ? 'text-warning' : undefined,
            },
            { rotulo: 'Lidos', valor: totals.read },
            {
              rotulo: 'Aparelhos ativos',
              valor: deviceInsights?.activeLast30d ?? '—',
              extra: deviceInsights && (
                <span className="ml-1 text-xs font-medium text-text-secondary">
                  de {deviceInsights.totalDevices}
                </span>
              ),
            },
          ]}
        />
        {deviceInsights && (
          <p className="px-1 text-xs text-text-secondary">
            <span
              className={
                deviceInsights.inactiveLast30d > 0
                  ? 'font-semibold tabular-nums text-warning'
                  : 'font-semibold tabular-nums text-text-title'
              }
            >
              {deviceInsights.inactiveLast30d}{' '}
              {deviceInsights.inactiveLast30d === 1 ? 'inativo' : 'inativos'}
            </span>{' '}
            sem abrir o app há mais de 30 dias
          </p>
        )}
      </div>

      {/* Com dados, o cartão de fora sai: a tabela tem o seu e a ListaMobile já
          é um bloco próprio. */}
      <div
        className={cn(
          'w-full animate-fade-up',
          (isLoading || list.length === 0) &&
            'overflow-hidden rounded-xl border border-border bg-card shadow-card'
        )}
      >
        {isLoading ? (
          <div className="overflow-x-auto">
            <TabelaCartoes className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-6 py-3">Notificação</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Lidos</th>
                  <th className="px-6 py-3">Criada em</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
            </TabelaCartoes>
            <TableSkeleton rows={3} columns={['w-40', 'w-24', 'w-16', 'w-24', 'w-24']} />
          </div>
        ) : list.length > 0 ? (
          <>
            <ListaMobile className="lg:hidden">
              {list.map((notification) => {
                const tipo = TYPE_MOBILE[notification.type];
                const Icone = tipo.icone;
                return (
                  <ItemLista
                    key={notification.id}
                    inicio={
                      <IconeQuadrado className={tipo.quadrado}>
                        <Icone size={20} />
                      </IconeQuadrado>
                    }
                    sobretitulo={
                      <>
                        <span
                          className={cn(
                            'text-[11px] font-semibold uppercase tracking-wide',
                            tipo.texto
                          )}
                        >
                          {TYPE_LABEL[notification.type]}
                        </span>
                        <span className="text-text-hint">
                          {new Date(notification.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </>
                    }
                    titulo={notification.title}
                    apoio={notification.body}
                    quebrar
                    meta={
                      <>
                        <Eye size={13} className="shrink-0" aria-hidden="true" />
                        <span className="tabular-nums">
                          {notification.readCount} {notification.readCount === 1 ? 'lido' : 'lidos'}
                        </span>
                        <span>· {deliveryLabel(notification)}</span>
                        {notification.lastDispatchedAt && (
                          <span className="truncate tabular-nums">
                            · {notification.lastSent ?? 0} enviados
                            {(notification.lastFailed ?? 0) > 0 &&
                              `, ${notification.lastFailed} falhas`}
                          </span>
                        )}
                      </>
                    }
                    acao={
                      <MenuAcoesNotificacao
                        notification={notification}
                        dispatching={dispatchingId === notification.id}
                        engagementLoading={engagementLoadingId === notification.id}
                        onDispatch={() => handleDispatch(notification)}
                        onEngagement={() => handleEngagement(notification)}
                        onDelete={() => handleDelete(notification)}
                      />
                    }
                  />
                );
              })}
            </ListaMobile>
            <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
              <div className="overflow-x-auto">
                <TabelaCartoes className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                    <tr>
                      <th className="px-6 py-3">Notificação</th>
                      <th className="px-6 py-3">Tipo</th>
                      <th className="px-6 py-3">Lidos</th>
                      <th className="px-6 py-3">Criada em</th>
                      <th className="px-6 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {list.map((notification) => (
                      <tr key={notification.id} className="hover:bg-muted/30">
                        <td className="px-6 py-4">
                          <span className="font-medium text-text-title">{notification.title}</span>
                          <span className="block max-w-xs truncate text-xs text-text-hint">
                            {notification.body}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_BADGE[notification.type]}`}
                          >
                            {TYPE_LABEL[notification.type]}
                          </span>
                        </td>
                        <td className="px-6 py-4 tabular-nums text-text-body">
                          {notification.readCount}
                          {notification.lastDispatchedAt && (
                            <span className="block text-xs text-text-hint">
                              {notification.lastSent} enviados
                              {(notification.lastFailed ?? 0) > 0 &&
                                `, ${notification.lastFailed} falhas`}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-text-body">
                          {new Date(notification.createdAt).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {canManualDispatch(notification) ? (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleDispatch(notification)}
                                disabled={dispatchingId === notification.id}
                                className="text-xs"
                              >
                                <Send size={14} />
                                {dispatchingId === notification.id ? 'Disparando...' : 'Disparar'}
                              </Button>
                            ) : (
                              <span className="text-xs text-text-hint">
                                {notification.type === 'task'
                                  ? 'Automático'
                                  : 'Só banner (push desligado)'}
                              </span>
                            )}
                            {notification.lastDispatchedAt && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEngagement(notification)}
                                disabled={engagementLoadingId === notification.id}
                                className="text-xs"
                                aria-label="Ver engajamento (GA4)"
                              >
                                <BarChart3 size={14} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(notification)}
                              aria-label="Excluir notificação"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TabelaCartoes>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
                <Bell size={26} className="text-primary" />
              </div>
              <p className="mb-1 text-lg font-medium text-text-title">Nenhuma notificação criada</p>
              <p className="text-sm text-text-secondary">Crie a primeira notificação.</p>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm max-md:items-end max-md:p-0">
          <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-elevated max-h-[90vh] overflow-y-auto animate-fade-up max-md:max-h-[92dvh] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card p-6">
              <h2 className="text-2xl font-semibold text-text-title">Nova notificação</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeModal}
                disabled={createMutation.isPending}
                aria-label="Fechar"
                className="rounded-full"
              >
                <X size={20} />
              </Button>
            </div>

            <div className="p-6">
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <div>
                  <label className={labelClass}>Tipo *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as NotificationType })}
                    className={inputClass}
                  >
                    {CREATABLE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {TYPE_LABEL[type]}
                      </option>
                    ))}
                  </select>
                </div>

                {(form.type === 'ad' || form.type === 'update') && (
                  <label className="flex items-center gap-2 text-sm text-text-title">
                    <input
                      type="checkbox"
                      checked={form.sendPush}
                      onChange={(e) => setForm({ ...form, sendPush: e.target.checked })}
                      className="size-4 rounded border-border text-primary focus:ring-primary/30"
                    />
                    Enviar push (desmarcado = só banner in-app)
                  </label>
                )}

                <div>
                  <label className={labelClass}>Título *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className={inputClass}
                    placeholder="ex: Leitor HL932 com 15% de desconto"
                  />
                </div>

                {form.type !== 'update' && (
                  <div>
                    <label className={labelClass}>Corpo *</label>
                    <textarea
                      value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      className={inputClass}
                      rows={3}
                      placeholder="Texto que aparece no banner e na notificação"
                    />
                  </div>
                )}

                {form.type === 'update' && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className={labelClass.replace('mb-2', 'mb-0')}>
                        Novidades desta versão *
                      </label>
                      <Button type="button" variant="secondary" size="sm" onClick={addItem}>
                        <Plus size={14} />
                        Adicionar novidade
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.items.map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => updateItem(index, e.target.value)}
                            className={inputClass}
                            placeholder="ex: Novo relatório de GMD por lote"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            aria-label="Remover novidade"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                      {form.items.length === 0 && (
                        <p className="text-sm text-text-hint">Nenhuma novidade adicionada ainda.</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Link (opcional)</label>
                  <input
                    type="text"
                    value={form.link}
                    onChange={(e) => setForm({ ...form, link: e.target.value })}
                    className={inputClass}
                    placeholder="ex: /produtos/hl932"
                  />
                </div>

                {form.type === 'ad' && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className={labelClass.replace('mb-2', 'mb-0')}>
                        Imagens (0 = só texto, 1 = banner, 2+ = carrossel)
                      </label>
                      <Button type="button" variant="secondary" size="sm" onClick={addImage}>
                        <ImageIcon size={14} />
                        Adicionar imagem
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.images.map((img, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={img.url}
                            onChange={(e) => updateImage(index, { url: e.target.value })}
                            className={inputClass}
                            placeholder="URL da imagem"
                          />
                          <input
                            type="text"
                            value={img.link ?? ''}
                            onChange={(e) => updateImage(index, { link: e.target.value })}
                            className={inputClass}
                            placeholder="Link (opcional)"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeImage(index)}
                            aria-label="Remover imagem"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {form.type === 'ad' && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className={labelClass.replace('mb-2', 'mb-0')}>
                        Destaques (opcional — formato: valor|rótulo)
                      </label>
                      <Button type="button" variant="secondary" size="sm" onClick={addItem}>
                        <Plus size={14} />
                        Adicionar destaque
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.items.map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => updateItem(index, e.target.value)}
                            className={inputClass}
                            placeholder="ex: 1,5 m|de alcance"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            aria-label="Remover destaque"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                      {form.items.length === 0 && (
                        <p className="text-sm text-text-hint">
                          Sem destaques o card mostra só a descrição. Só os 2 primeiros aparecem no
                          app.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {form.type === 'update' && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>Versão mínima exigida</label>
                      <input
                        type="text"
                        value={form.minVersion}
                        onChange={(e) => setForm({ ...form, minVersion: e.target.value })}
                        className={inputClass}
                        placeholder="ex: 2.4.0"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 text-sm text-text-title">
                        <input
                          type="checkbox"
                          checked={form.force}
                          onChange={(e) => setForm({ ...form, force: e.target.checked })}
                          className="size-4 rounded border-border text-primary focus:ring-primary/30"
                        />
                        Forçar atualização (bloqueia o app)
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 border-t border-border pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeModal}
                    disabled={createMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Salvando...' : 'Criar notificação'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
